import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SignatureCanvas } from '@/components/SignatureCanvas';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEXTS = {
  is: {
    title: 'Ráðningarsamningur til undirritunar',
    intro: 'Lestu samninginn vandlega yfir. Teiknaðu svo undirskrift þína í reitinn og smelltu á „Undirrita samning".',
    open_pdf: 'Opna PDF í nýjum glugga',
    sign_label: 'Undirskrift þín (teiknaðu með fingri eða mús)',
    sign_btn: 'Undirrita samning',
    signing: 'Undirrita...',
    signed_title: 'Samningur undirritaður!',
    signed_text: 'Takk fyrir. Afrit af undirrituðum samningi hefur verið sent í tölvupósti.',
    already_signed: 'Þessi samningur hefur þegar verið undirritaður.',
    not_found: 'Samningur fannst ekki eða hlekkurinn er útrunninn.',
    need_sig: 'Teiknaðu undirskrift fyrst',
  },
  en: {
    title: 'Employment contract for signature',
    intro: 'Please read the contract carefully. Then draw your signature in the box and click "Sign contract".',
    open_pdf: 'Open PDF in a new window',
    sign_label: 'Your signature (draw with finger or mouse)',
    sign_btn: 'Sign contract',
    signing: 'Signing...',
    signed_title: 'Contract signed!',
    signed_text: 'Thank you. A copy of the signed contract has been sent by email.',
    already_signed: 'This contract has already been signed.',
    not_found: 'Contract not found or the link has expired.',
    need_sig: 'Please draw your signature first',
  },
};

export default function SignContract() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [signature, setSignature] = useState(null);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/contracts/sign/${token}`);
        setInfo(res.data);
        if (res.data.pdf_base64) {
          const bytes = Uint8Array.from(atob(res.data.pdf_base64), (c) => c.charCodeAt(0));
          const blob = new Blob([bytes], { type: 'application/pdf' });
          setPdfUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        setError(err.response?.status === 404 ? 'not_found' : 'not_found');
      }
    };
    load();
  }, [token]);

  const onSigChange = useCallback((sig) => setSignature(sig), []);

  const t = TEXTS[info?.language === 'en' ? 'en' : 'is'];

  const handleSign = async () => {
    if (!signature) {
      toast.error(t.need_sig);
      return;
    }
    setSigning(true);
    try {
      await axios.post(`${API}/contracts/sign/${token}`, { signature });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Villa / Error');
    } finally {
      setSigning(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 text-center max-w-md" data-testid="sign-error">
          <p className="text-lg font-semibold text-red-600">{TEXTS.is.not_found}</p>
          <p className="text-sm text-gray-500 mt-2">{TEXTS.en.not_found}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (done || info.status === 'signed') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow p-8 text-center max-w-md" data-testid="sign-success">
          <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold">{done ? t.signed_title : t.already_signed}</h1>
          {done && <p className="text-sm text-gray-600 mt-2">{t.signed_text}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">
      <div className="max-w-3xl mx-auto space-y-4" data-testid="sign-page">
        <div className="bg-white rounded-xl shadow p-6">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            {t.title}
          </h1>
          <p className="text-sm text-gray-600 mt-1">{info.employee_name}</p>
          <p className="text-sm text-gray-500 mt-2">{t.intro}</p>
        </div>

        {pdfUrl && (
          <div className="bg-white rounded-xl shadow p-2">
            <iframe src={pdfUrl} title="contract" className="w-full rounded-md" style={{ height: '65vh' }} data-testid="contract-pdf-frame" />
            <div className="p-2">
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-sm text-blue-600 underline" data-testid="open-pdf-link">
                {t.open_pdf}
              </a>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <SignatureCanvas label={t.sign_label} onChange={onSigChange} />
          <Button onClick={handleSign} disabled={signing} className="w-full text-base py-6" data-testid="sign-contract-btn">
            {signing ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle2 className="mr-2 h-5 w-5" />}
            {signing ? t.signing : t.sign_btn}
          </Button>
        </div>
      </div>
    </div>
  );
}
